import type React from 'react';
import type { BoundaryEvidence, Confidence, SiteConstraint } from '../api/locate';
import { DesignationDisclaimer, TransportDisclaimer } from './SiteMapView';
import { AI_DISCLAIMER } from '../legal';

/** The year in the Crown and other copyright lines: the data is current as of now. */
const YEAR = new Date().getFullYear();

export const CONFIDENCE_CLASS: Record<Confidence, string> = {
  High: 'sx-chip-pos',
  Medium: 'sx-chip-warn',
  Low: 'sx-chip-warn',
  'Not established': 'sx-chip-neg',
};

interface StoredBoundary {
  areaHa?: number;
  confidence?: Confidence;
  documentName?: string;
  scaleDenominator?: number;
  evidence?: BoundaryEvidence[];
  warnings?: string[];
  userEdited?: boolean;
}

interface StoredLocation {
  constraints?: SiteConstraint[];
  confidence?: Confidence;
  evidence?: string[];
  warnings?: string[];
  userEdited?: boolean;
  boundary?: StoredBoundary;
}

function parse(siteLocation: string | null | undefined): StoredLocation | null {
  if (!siteLocation) return null;
  try { return JSON.parse(siteLocation) as StoredLocation; } catch { return null; }
}

function confidenceChip(confidence: Confidence | undefined, edited: boolean | undefined, editedText: string, extra = '') {
  const c: Confidence = confidence ?? 'Not established';
  const text = edited ? editedText : c === 'Not established' ? 'Location not established' : `${c} confidence`;
  return <span className={`sx-chip ${edited ? 'sx-chip-pos' : CONFIDENCE_CLASS[c]}`}>{text}{extra}</span>;
}

/** The AI analysis disclaimer, in the same collapsed section as the others. */
function AnalysisDisclaimer() {
  return <div className="sx-note"><strong>About this analysis.</strong> {AI_DISCLAIMER}</div>;
}

function DisclaimerSection({ children }: { children: React.ReactNode }) {
  return (
    <section>
      <details className="lm-all">
        <summary>
          <div className="lm-sum">
            <h2 className="sx-sec-h">Disclaimer</h2>
          </div>
        </summary>
        {children}
      </details>
    </section>
  );
}

/**
 * The saved record of how a case's site was located and how its outline was read, as two dropdowns. `analysis` adds
 * the AI analysis disclaimer to the Disclaimer section, which then shows even for a case with no saved location.
 */
export function SiteLocationNotes({ siteLocation, explainMissingOutline = false, analysis = false }: { siteLocation: string | null | undefined; explainMissingOutline?: boolean; analysis?: boolean }) {
  const loc = parse(siteLocation);
  if (!loc) return analysis ? <DisclaimerSection><AnalysisDisclaimer /></DisclaimerSection> : null;
  const evidence = Array.isArray(loc.evidence) ? loc.evidence : [];
  const warnings = Array.isArray(loc.warnings) ? loc.warnings : [];
  const b = loc.boundary;
  const bEvidence = Array.isArray(b?.evidence) ? b.evidence : [];
  const bWarnings = Array.isArray(b?.warnings) ? b.warnings : [];
  const area = typeof b?.areaHa === 'number' ? ` · ${b.areaHa.toFixed(b.areaHa < 1 ? 2 : 1)} ha` : '';
  const constraints = Array.isArray(loc.constraints) ? loc.constraints : [];
  const hasDesignations = constraints.some(c => c.group !== 'Transport');
  const hasTransport = constraints.some(c => c.group === 'Transport' && c.status === 'Present');

  return (
    <>
      <section>
        <details className="lm-all">
          <summary>
            <div className="lm-sum">
              <h2 className="sx-sec-h">How we found the location</h2>
              {confidenceChip(loc.confidence, loc.userEdited, 'Set by you')}
            </div>
          </summary>
          {evidence.length > 0
            ? (
              <ul className="lm-list">
                {evidence.map((e, i) => <li key={i} className={e.startsWith('The documents say:') ? 'lm-quote' : undefined}>{e}</li>)}
              </ul>
            )
            : <div className="sx-note">{loc.userEdited ? 'The location was set by hand.' : 'No further detail was recorded for this location.'}</div>}
          {warnings.length > 0 && <div className="lm-warnings" role="note">{warnings.map((w, i) => <div key={i}>{w}</div>)}</div>}
        </details>
      </section>

      {!b && explainMissingOutline && (
        <section>
          <h2 className="sx-sec-h">How we read the outline</h2>
          <div className="sx-note">No site outline was mapped for this case, so the location is a single point and designations were searched in a circle around it.</div>
        </section>
      )}

      {b && (
        <section>
          <details className="lm-all">
            <summary>
              <div className="lm-sum">
                <h2 className="sx-sec-h">How we read the outline</h2>
                {confidenceChip(b.confidence ?? 'Low', b.userEdited, 'Outline adjusted by you', area)}
              </div>
            </summary>
            <ul className="lm-list">
              {b.documentName && <li>From “{b.documentName}”{b.scaleDenominator ? ` (scale 1:${b.scaleDenominator.toLocaleString('en-GB')})` : ''}.</li>}
              {bEvidence.map((e, i) => <li key={i}><strong>{e.label}</strong>{e.detail ? `: ${e.detail}` : ''}</li>)}
            </ul>
            {bWarnings.length > 0 && <div className="lm-warnings" role="note">{bWarnings.map((w, i) => <div key={i}>{w}</div>)}</div>}
          </details>
        </section>
      )}
      <section>
        <details className="lm-all">
          <summary>
            <div className="lm-sum">
              <h2 className="sx-sec-h">Data sources</h2>
            </div>
          </summary>
          <ul className="lm-list">
            <li>Contains OS data © Crown copyright and database right {YEAR} (OS Open Names, OS OpenMap – Local). Open Government Licence v3.0.</li>
            <li>Contains OS data © Crown copyright and database rights {YEAR} (OS MasterMap Topography Layer, via the OS Data Hub), used to place the site outline.</li>
            <li>Contains HM Land Registry data © Crown copyright and database right {YEAR} (INSPIRE Index Polygons: registered freehold land in England and Wales, indicative). Open Government Licence v3.0.</li>
            <li>Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors (Open Database Licence).</li>
            <li>Planning designations from planning.data.gov.uk, including Historic England data © Historic England {YEAR}. Open Government Licence v3.0.</li>
            <li>Contains Natural England data © Natural England copyright {YEAR} (priority habitats, agricultural land classification, ancient and long-established woodland and other designations). Open Government Licence v3.0.</li>
            <li>Contains Environment Agency information © Environment Agency and/or database right {YEAR} (flood risk). Open Government Licence v3.0.</li>
            <li>Contains Mining Remediation Authority data © Mining Remediation Authority {YEAR} (coal mining areas). Open Government Licence v3.0.</li>
            <li>Public transport stops and stations from NaPTAN, and bus timetables from the Bus Open Data Service (Department for Transport). Open Government Licence v3.0.</li>
          </ul>
        </details>
      </section>
      {(analysis || hasDesignations || hasTransport) && (
        <DisclaimerSection>
          {analysis && <AnalysisDisclaimer />}
          {hasDesignations && <DesignationDisclaimer />}
          {hasTransport && <TransportDisclaimer constraints={constraints} />}
        </DisclaimerSection>
      )}
    </>
  );
}
